import React, { useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'

interface S3ImageModalProps {
  url: string
  alt?: string
  onClose: () => void
}

/**
 * Full-screen lightbox overlay for viewing an S3 image at full size.
 * Renders via React Portal so it sits above the chat panel.
 * Closes on backdrop click, Escape key, or the close button.
 */
export const S3ImageModal: React.FC<S3ImageModalProps> = ({ url, alt = 'Image', onClose }) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  const handleDownload = useCallback(() => {
    const a = document.createElement('a')
    a.href = url
    a.download = alt || 'image.png'
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.click()
  }, [url, alt])

  return ReactDOM.createPortal(
    <div className='s3-image-modal' onClick={handleBackdropClick} role='dialog' aria-label={alt}>
      <div className='s3-image-modal__content'>
        <div className='s3-image-modal__toolbar'>
          <button className='s3-image-modal__btn' onClick={handleDownload} title='Download'>
            <i className='fas fa-download' aria-hidden />
          </button>
          <button className='s3-image-modal__btn' onClick={onClose} title='Close'>
            <i className='fas fa-xmark' aria-hidden />
          </button>
        </div>
        <img src={url} alt={alt} className='s3-image-modal__img' />
      </div>
    </div>,
    document.body,
  )
}
